import { useEffect, useMemo } from "react";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { Clock3, ExternalLink, MapPinned, MessageCircle, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BRANCHES } from "@/lib/branches";
import { dictionary } from "@/i18n/translations";
import { setDocumentMeta } from "@/lib/seo";
import "leaflet/dist/leaflet.css";

let markerIconConfigured = false;

const configureMarkerIcon = () => {
  if (markerIconConfigured) {
    return;
  }

  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });

  markerIconConfigured = true;
};

const createBranchMarkerIcon = (label, order) =>
  L.divIcon({
    className: "branches-map-marker-shell",
    html: `
      <div class="branches-map-marker" data-testid="branches-page-marker-${order}">
        <span class="branches-map-marker-index">${order}</span>
        <span class="branches-map-marker-label">${label}</span>
      </div>
    `,
    iconSize: [150, 44],
    iconAnchor: [34, 44],
  });

const FitBranchesBounds = ({ branches }) => {
  const map = useMap();

  useEffect(() => {
    const bounds = L.latLngBounds(
      branches.map((branch) => [branch.coordinates.lat, branch.coordinates.lng]),
    );

    map.fitBounds(bounds.pad(0.55), {
      animate: true,
      duration: 1.4,
    });
  }, [branches, map]);

  return null;
};

const BranchCard = ({ branch, copy, lang }) => {
  const addressLines = branch.address[lang] || branch.address.ar;
  const hoursLines = branch.hours[lang] || branch.hours.ar;
  const name = branch.name[lang] || branch.name.ar;
  const city = branch.city[lang] || branch.city.ar;
  const note = branch.note[lang] || branch.note.ar;

  return (
    <Card className="branch-card border-gold-500/20 bg-white/90 shadow-[0_24px_60px_rgba(29,18,7,0.16)]" data-testid={`branches-page-card-${branch.id}`}>
      <CardContent className="space-y-5 p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="branch-card-order" data-testid={`branches-page-card-order-${branch.id}`}>
              {branch.order}
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-stone-900" data-testid={`branches-page-card-title-${branch.id}`}>
                {name}
              </h2>
              <p className="text-sm text-stone-500" data-testid={`branches-page-card-city-${branch.id}`}>
                {city}
              </p>
            </div>
          </div>

          <div className="branch-card-chip" data-testid={`branches-page-card-chip-${branch.id}`}>
            <MapPinned size={14} />
            <span>{city}</span>
          </div>
        </div>

        <p className="text-sm leading-7 text-stone-600" data-testid={`branches-page-card-note-${branch.id}`}>
          {note}
        </p>

        <div className="space-y-3">
          <div className="branch-card-meta-row" data-testid={`branches-page-card-phone-${branch.id}`}>
            <PhoneCall size={16} />
            <a href={branch.phone_link} className="transition hover:text-stone-900">
              {branch.phone_display}
            </a>
          </div>

          <div className="branch-card-meta-row items-start" data-testid={`branches-page-card-address-${branch.id}`}>
            <MapPinned size={16} className="mt-1" />
            <div className="space-y-1">
              {addressLines.map((line) => (
                <p key={`${branch.id}-${line}`} className="text-sm text-stone-600">
                  {line}
                </p>
              ))}
            </div>
          </div>

          <div className="branch-card-meta-row items-start" data-testid={`branches-page-card-hours-${branch.id}`}>
            <Clock3 size={16} className="mt-1" />
            <div className="space-y-1">
              {hoursLines.map((line) => (
                <p key={`${branch.id}-hours-${line}`} className="text-sm text-stone-600">
                  {line}
                </p>
              ))}
            </div>
          </div>
        </div>

        {branch.approximate_map_position && (
          <div className="branch-card-note" data-testid={`branches-page-card-approximate-${branch.id}`}>
            {copy.branchesPage.approximateMapNotice}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button asChild className="rounded-full bg-stone-900 px-5 text-cream-50 hover:bg-stone-700">
            <a href={branch.map_url} target="_blank" rel="noreferrer" data-testid={`branches-page-card-maps-link-${branch.id}`}>
              <ExternalLink size={15} />
              <span>{copy.branchesPage.openMaps}</span>
            </a>
          </Button>

          <Button asChild variant="outline" className="rounded-full border-gold-500/40 bg-white/80 px-5 text-stone-800 hover:bg-amber-50">
            <a href={branch.whatsapp_url} target="_blank" rel="noreferrer" data-testid={`branches-page-card-whatsapp-link-${branch.id}`}>
              <MessageCircle size={15} />
              <span>{copy.branchesPage.whatsapp}</span>
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function BranchesPage({ lang }) {
  const copy = dictionary[lang];

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: copy.seo.branchesTitle,
      description: copy.seo.branchesDescription,
    });
  }, [copy.seo.branchesDescription, copy.seo.branchesTitle, lang]);

  configureMarkerIcon();

  const localizedBranches = useMemo(
    () =>
      BRANCHES.map((branch) => ({
        ...branch,
        markerIcon: createBranchMarkerIcon(branch.city[lang] || branch.city.ar, branch.order),
      })),
    [lang],
  );

  return (
    <div className="min-h-screen bg-luxury-surface pt-28" data-testid="branches-page-root">
      <main className="mx-auto max-w-7xl space-y-10 px-4 pb-24 sm:px-10" data-testid="branches-page-main">
        <section className="branches-hero-panel" data-testid="branches-page-hero">
          <div className="max-w-3xl space-y-5">
            <h1 className="text-4xl font-semibold leading-tight text-stone-950 sm:text-5xl lg:text-6xl" data-testid="branches-page-title">
              {copy.branchesPage.title}
            </h1>

            <p className="max-w-2xl text-base leading-8 text-stone-600 sm:text-lg" data-testid="branches-page-subtitle">
              {copy.branchesPage.subtitle}
            </p>
          </div>

          <div className="branches-stat-pill" data-testid="branches-page-stat-pill">
            <span className="branches-stat-number">{String(BRANCHES.length).padStart(2, "0")}</span>
            <span>{copy.branchesPage.statLabel}</span>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.12fr_0.88fr]" data-testid="branches-page-atlas-grid">
          <div className="branches-atlas-shell" data-testid="branches-page-map-shell">
            <div className="branches-atlas-copy">
              <p className="text-xs uppercase tracking-[0.35em] text-gold-200/80">{copy.branchesPage.mapEyebrow}</p>
              <h2 className="text-2xl font-semibold text-cream-50 sm:text-3xl">{copy.branchesPage.mapTitle}</h2>
              <p className="max-w-xl text-sm leading-7 text-[#eadbc1] sm:text-base">
                {copy.branchesPage.mapDescription}
              </p>
            </div>

            <div className="branches-map-frame" data-testid="branches-page-map-frame">
              <MapContainer
                center={[21.34, 40.12]}
                zoom={8}
                scrollWheelZoom
                zoomControl={false}
                className="h-full w-full"
                data-testid="branches-page-map"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <FitBranchesBounds branches={localizedBranches} />

                {localizedBranches.map((branch) => (
                  <Marker
                    key={branch.id}
                    position={[branch.coordinates.lat, branch.coordinates.lng]}
                    icon={branch.markerIcon}
                  >
                    <Popup>
                      <div className="space-y-2 text-sm">
                        <strong>{branch.name[lang] || branch.name.ar}</strong>
                        <p>{(branch.address[lang] || branch.address.ar).join(" - ")}</p>
                        <a href={branch.map_url} target="_blank" rel="noreferrer" className="text-[#8c662d] underline">
                          {copy.branchesPage.openMaps}
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          <div className="space-y-5" data-testid="branches-page-cards-column">
            {localizedBranches.map((branch) => (
              <BranchCard key={branch.id} branch={branch} copy={copy} lang={lang} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
